import { xml, yaml, type GeneratedFile } from './files';

/** `Off | Both | Bearer` (nyce-logic-server `docs/auth-client-credentials.md`). */
export type ServiceAuth = 'Off' | 'Both' | 'Bearer';

export interface WorkServerOptionsInput {
  /** `{authUrl}/api/auth/v1`. */
  authServiceUrl: string;
  /** User sessions (nyceBuddy3 / AUTH sign-in): `Auth/Authority`, `Auth/Audience`, `Auth/ServiceKey`. */
  userSessions?: { authority: string; serviceKey: string };
  /** The installation's service client (`client_credentials`). */
  client?: { clientId: string; clientSecret: string; secretIn: 'env' | 'options' };
  datacollector?: { uri: string; token?: string; auth: ServiceAuth };
  boxCalculation?: { serviceUrl: string; auth: ServiceAuth };
  printQueue?: { serviceUrl: string; token?: string; auth: ServiceAuth };
}

const el = (indent: string, name: string, value: string | undefined, comment?: string) =>
  value === undefined
    ? ''
    : `${indent}<${name}>${xml(value)}</${name}>${comment ? `   <!-- ${comment} -->` : ''}\n`;

/**
 * The WorkServer options fragment (`nycelogicworkserver.nlconfig` `<Options>` or the `ServerOptions` row) for the
 * services the wizard set up. Read at WorkServer start: restart after changing.
 */
export function workServerOptionsXml(o: WorkServerOptionsInput): string {
  let out =
    '<!-- Merge into <Options> of nycelogicworkserver.nlconfig (or the ServerOptions row), then restart the WorkServer. -->\n';
  const c = o.client;
  out += '<Auth>\n';
  out += el('  ', 'ServiceUrl', o.authServiceUrl);
  if (o.userSessions) {
    out += el('  ', 'Authority', o.userSessions.authority);
    out += el('  ', 'Audience', 'nyce.logic');
    out += el(
      '  ',
      'ServiceKey',
      o.userSessions.serviceKey,
      'tenant service key (user sign-in through AUTH)'
    );
  }
  if (c?.secretIn === 'options') {
    out += el('  ', 'ClientId', c.clientId);
    out += el('  ', 'ClientSecret', c.clientSecret, 'prefer env AUTH_CLIENT_SECRET where possible');
  } else if (c)
    out +=
      '  <!-- ClientId / ClientSecret: from env AUTH_CLIENT_ID / AUTH_CLIENT_SECRET (Kubernetes secret workserver-auth) -->\n';
  out += '</Auth>\n';
  if (o.datacollector) {
    out += '<RemoteDataCollector>\n';
    out += el('  ', 'Uri', o.datacollector.uri);
    out += el('  ', 'Token', o.datacollector.token);
    out += el('  ', 'Alarms', 'true');
    out += el('  ', 'Metrics', 'true');
    out += el('  ', 'Auth', o.datacollector.auth, 'Off | Both | Bearer');
    out += '</RemoteDataCollector>\n';
  }
  if (o.boxCalculation) {
    out += '<Custom>\n  <BoxCalculation>\n';
    out += el('    ', 'ServiceUrl', o.boxCalculation.serviceUrl);
    out += el('    ', 'Auth', o.boxCalculation.auth, 'Off | Both | Bearer');
    out += '  </BoxCalculation>\n</Custom>\n';
  }
  if (o.printQueue) {
    out += '<PrintQueue>\n';
    out += el('  ', 'ServiceUrl', o.printQueue.serviceUrl);
    out += el('  ', 'Token', o.printQueue.token, "keep: admin screens and Both's fallback");
    out += el('  ', 'Auth', o.printQueue.auth, 'Off | Both | Bearer');
    out += '</PrintQueue>\n';
  }
  return out;
}

/** The Kubernetes secret the hosting manifests map to `AUTH_CLIENT_ID` / `AUTH_CLIENT_SECRET` (S3). */
export function workServerSecretYaml(namespace: string, clientId: string, clientSecret: string): string {
  return `apiVersion: v1
kind: Secret
metadata:
  name: workserver-auth
  namespace: ${yaml(namespace)}
type: Opaque
stringData:
  clientId: ${yaml(clientId)}
  clientSecret: ${yaml(clientSecret)}
`;
}

export const workServerRestart = (namespace: string) =>
  `kubectl -n ${namespace} apply -f workserver-auth.yaml\nkubectl -n ${namespace} rollout restart deployment/workserver-system deployment/workserver-client\n`;

/** WorkServer files for a GKE installation (options + secret + commands) or an on-prem one (options only). */
export function workServerFiles(o: WorkServerOptionsInput, namespace?: string): GeneratedFile[] {
  const files: GeneratedFile[] = [
    {
      name: 'workserver-options.xml',
      content: workServerOptionsXml(o),
      note: 'Merge into the WorkServer options (<Options> of nycelogicworkserver.nlconfig, or the ServerOptions row).',
    },
  ];
  if (namespace && o.client?.secretIn === 'env') {
    files.push(
      {
        name: 'workserver-auth.yaml',
        content: workServerSecretYaml(namespace, o.client.clientId, o.client.clientSecret),
        note: 'The hosting manifests map it to AUTH_CLIENT_ID / AUTH_CLIENT_SECRET (optional secret).',
      },
      {
        name: 'apply.sh',
        content: workServerRestart(namespace),
        note: 'Apply the secret and restart the WorkServers.',
      }
    );
  }
  return files;
}
